import React, { useState, useEffect, useRef, useCallback } from 'react';

// ============================================================================
// CONSTANTS
// ============================================================================
const OPTIMAL_ACCURACY = 0.794; // Research-backed sweet spot (79.4%)
const FLOW_ZONE_MIN = 0.65;
const FLOW_ZONE_MAX = 0.85;

// ============================================================================
// LUCIDE-STYLE SVG ICONS
// ============================================================================
const Icons = {
  // Brain/Neural network icon for intro
  Brain: ({ size = 24, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"/>
      <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"/>
      <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"/>
      <path d="M17.599 6.5a3 3 0 0 0 .399-1.375"/>
      <path d="M6.003 5.125A3 3 0 0 0 6.401 6.5"/>
      <path d="M3.477 10.896a4 4 0 0 1 .585-.396"/>
      <path d="M19.938 10.5a4 4 0 0 1 .585.396"/>
      <path d="M6 18a4 4 0 0 1-1.967-.516"/>
      <path d="M19.967 17.484A4 4 0 0 1 18 18"/>
    </svg>
  ),
  // Zap icon for complexity note
  Zap: ({ size = 24, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  ),
  // Lightbulb icon for pro tips
  Lightbulb: ({ size = 24, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/>
      <path d="M9 18h6"/>
      <path d="M10 22h4"/>
    </svg>
  ),
  // Target icon for short-term goals
  Target: ({ size = 24, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <circle cx="12" cy="12" r="10"/>
      <circle cx="12" cy="12" r="6"/>
      <circle cx="12" cy="12" r="2"/>
    </svg>
  ),
  // TrendingUp icon for long-term benefits
  TrendingUp: ({ size = 24, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/>
      <polyline points="16 7 22 7 22 13"/>
    </svg>
  ),
  // BookOpen icon for research
  BookOpen: ({ size = 24, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
    </svg>
  ),
  // Loader icon for loading state
  Loader: ({ size = 24, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <line x1="12" y1="2" x2="12" y2="6"/>
      <line x1="12" y1="18" x2="12" y2="22"/>
      <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/>
      <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/>
      <line x1="2" y1="12" x2="6" y2="12"/>
      <line x1="18" y1="12" x2="22" y2="12"/>
      <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/>
      <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
    </svg>
  ),
  // Check icon for success state
  Check: ({ size = 24, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  // Sparkles icon for AI coach
  Sparkles: ({ size = 24, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
      <path d="M5 3v4"/>
      <path d="M19 17v4"/>
      <path d="M3 5h4"/>
      <path d="M17 19h4"/>
    </svg>
  ),
  // Play icon for tutorial
  Play: ({ size = 24, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <polygon points="5 3 19 12 5 21 5 3"/>
    </svg>
  ),
  // ChevronRight for progress
  ChevronRight: ({ size = 24, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  ),
  // Eye for watching
  Eye: ({ size = 24, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ),
  // Volume for audio
  Volume: ({ size = 24, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
    </svg>
  ),
  // VolumeX for muted state
  VolumeX: ({ size = 24, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
      <line x1="22" y1="9" x2="16" y2="15"/>
      <line x1="16" y1="9" x2="22" y2="15"/>
    </svg>
  ),
  // Trophy for achievements
  Trophy: ({ size = 24, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
      <path d="M4 22h16"/>
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
    </svg>
  ),
  // Flame for streaks
  Flame: ({ size = 24, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>
    </svg>
  ),
  // Calendar for tracking
  Calendar: ({ size = 24, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  // BarChart for analytics
  BarChart: ({ size = 24, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <line x1="12" y1="20" x2="12" y2="10"/>
      <line x1="18" y1="20" x2="18" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="16"/>
    </svg>
  ),
  // Hand pointer for clicking
  Hand: ({ size = 24, className = '' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0"/>
      <path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2"/>
      <path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v8"/>
      <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"/>
    </svg>
  ),
};

// ============================================================================
// VISUAL THEMES - Combat monotony with variety
// ============================================================================
const THEMES = {
  neural: {
    name: 'Neural',
    description: 'Classic biopunk aesthetic',
    primary: '100, 200, 255',     // Cyan
    secondary: '80, 160, 220',
    accent: '120, 200, 255',
    success: '80, 200, 120',
    error: '220, 80, 80',
    background: '8, 12, 24',
  },
  aurora: {
    name: 'Aurora',
    description: 'Northern lights inspired',
    primary: '100, 255, 200',     // Teal/green
    secondary: '80, 200, 180',
    accent: '150, 255, 220',
    success: '100, 255, 180',
    error: '255, 100, 120',
    background: '10, 20, 30',
  },
  sunset: {
    name: 'Sunset',
    description: 'Warm evening tones',
    primary: '255, 180, 100',     // Orange/amber
    secondary: '220, 140, 80',
    accent: '255, 200, 120',
    success: '180, 220, 100',
    error: '255, 100, 100',
    background: '20, 12, 15',
  },
  midnight: {
    name: 'Midnight',
    description: 'Deep purple focus',
    primary: '180, 140, 255',     // Purple
    secondary: '140, 100, 220',
    accent: '200, 160, 255',
    success: '140, 220, 180',
    error: '255, 120, 140',
    background: '12, 8, 20',
  },
};

// ============================================================================
// SESSION HISTORY STORAGE
// ============================================================================
const STORAGE_KEY = 'neural-sync-sessions';
const SETTINGS_KEY = 'neural-sync-settings';
const TUTORIAL_KEY = 'neural-sync-tutorial-complete';
const STREAK_KEY = 'neural-sync-streak';

const saveSession = (sessionData) => {
  try {
    const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    existing.push({
      ...sessionData,
      id: Date.now(),
      timestamp: new Date().toISOString(),
    });
    // Keep last 100 sessions
    const trimmed = existing.slice(-100);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch (e) {
    console.warn('Failed to save session:', e);
  }
};

const getSessions = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch (e) {
    return [];
  }
};

const saveSettings = (settings) => {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch (e) {
    console.warn('Failed to save settings:', e);
  }
};

const loadSettings = () => {
  try {
    const saved = localStorage.getItem(SETTINGS_KEY);
    if (saved) return JSON.parse(saved);
  } catch (e) {
    console.warn('Failed to load settings:', e);
  }
  return null;
};

const isTutorialComplete = () => {
  try {
    return localStorage.getItem(TUTORIAL_KEY) === 'true';
  } catch (e) {
    return false;
  }
};

const setTutorialComplete = () => {
  try {
    localStorage.setItem(TUTORIAL_KEY, 'true');
  } catch (e) {
    console.warn('Failed to save tutorial status:', e);
  }
};

// Streak tracking
const getStreak = () => {
  try {
    const data = JSON.parse(localStorage.getItem(STREAK_KEY) || '{}');
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();

    if (data.lastDate === today) {
      return { current: data.current || 0, trainedToday: true };
    } else if (data.lastDate === yesterday) {
      return { current: data.current || 0, trainedToday: false };
    } else {
      // Streak broken
      return { current: 0, trainedToday: false };
    }
  } catch (e) {
    return { current: 0, trainedToday: false };
  }
};

const updateStreak = () => {
  try {
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    const data = JSON.parse(localStorage.getItem(STREAK_KEY) || '{}');

    if (data.lastDate === today) {
      // Already trained today
      return data.current;
    } else if (data.lastDate === yesterday) {
      // Continue streak
      const newStreak = (data.current || 0) + 1;
      localStorage.setItem(STREAK_KEY, JSON.stringify({ current: newStreak, lastDate: today }));
      return newStreak;
    } else {
      // Start new streak
      localStorage.setItem(STREAK_KEY, JSON.stringify({ current: 1, lastDate: today }));
      return 1;
    }
  } catch (e) {
    return 1;
  }
};

// ============================================================================
// AI COACH - Hidden Complexity, Minimal Interface
// Flow state algorithm targeting 79.4% accuracy (research-backed sweet spot)
// Factors: accuracy + reaction time + consistency
// ============================================================================
const AICoach = {
  // Get a coaching tip based on current performance
  getTip: (stats, nLevel, sessions, reactionTimes = []) => {
    const tips = [];
    const positionAcc = stats.positionTotal > 0 ? stats.positionCorrect / stats.positionTotal : 0;
    const audioAcc = stats.audioTotal > 0 ? stats.audioCorrect / stats.audioTotal : 0;
    const overallAcc = stats.total > 0 ? stats.correct / stats.total : 0;

    // Only give tips after a few trials
    if (stats.trial < 5) return null;

    // Modality imbalance tips
    if (stats.positionTotal >= 3 && stats.audioTotal >= 3) {
      if (positionAcc > audioAcc + 0.25) {
        tips.push("Focus more on the letter sounds");
      } else if (audioAcc > positionAcc + 0.25) {
        tips.push("Watch the grid positions more closely");
      }
    }

    // Reaction time tips
    if (reactionTimes.length >= 5) {
      const avgRT = reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length;
      if (avgRT < 500 && overallAcc < 0.6) {
        tips.push("Slow down slightly - accuracy over speed");
      } else if (avgRT > 2000 && overallAcc > 0.75) {
        tips.push("Trust your instincts - respond faster");
      }
    }

    // Flow state tips
    if (overallAcc >= FLOW_ZONE_MAX && stats.trial >= 10) {
      tips.push("You're in the zone! Consider increasing difficulty");
    } else if (overallAcc < FLOW_ZONE_MIN && stats.trial >= 8) {
      tips.push("Take your time - trust your first instinct");
    } else if (overallAcc >= FLOW_ZONE_MIN && overallAcc <= FLOW_ZONE_MAX) {
      tips.push("Perfect challenge level - you're at the learning edge");
    }

    // Return a random tip or null
    return tips.length > 0 ? tips[Math.floor(Math.random() * tips.length)] : null;
  },

  // Generate end-of-session feedback with more detail
  getSessionFeedback: (stats, nLevel, reactionTimes = []) => {
    const overallAcc = stats.total > 0 ? stats.correct / stats.total : 0;
    const avgRT = reactionTimes.length > 0
      ? Math.round(reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length)
      : null;

    let feedback = '';

    // Accuracy-based feedback
    if (overallAcc >= 0.85) {
      feedback = "Outstanding! Moving up.";
    } else if (overallAcc >= OPTIMAL_ACCURACY - 0.05 && overallAcc <= OPTIMAL_ACCURACY + 0.05) {
      feedback = "Perfect sweet spot!";
    } else if (overallAcc >= FLOW_ZONE_MIN) {
      feedback = "Solid session. Building.";
    } else if (overallAcc >= 0.4) {
      feedback = "Challenging - adjusting down.";
    } else {
      feedback = "Tough one. Lowering difficulty.";
    }

    return feedback;
  },

  // Calculate optimal N-level based on history using flow state model
  // Target: 79.4% accuracy (OPTIMAL_ACCURACY)
  getOptimalLevel: (sessions) => {
    if (sessions.length < 3) return 2; // Default for new users

    const recent = sessions.slice(-10);
    const byLevel = {};

    recent.forEach(s => {
      if (!byLevel[s.nLevel]) byLevel[s.nLevel] = { accs: [], rts: [] };
      byLevel[s.nLevel].accs.push(s.accuracy);
      if (s.avgReactionTime) byLevel[s.nLevel].rts.push(s.avgReactionTime);
    });

    // Find the level closest to optimal accuracy (79.4%)
    let bestLevel = 2;
    let closestToOptimal = 1;

    Object.entries(byLevel).forEach(([level, data]) => {
      const avgAcc = data.accs.reduce((a, b) => a + b, 0) / data.accs.length;
      const distanceToOptimal = Math.abs(avgAcc - OPTIMAL_ACCURACY);

      // Weight by consistency (lower variance = more confidence)
      const variance = data.accs.length > 1
        ? data.accs.reduce((sum, acc) => sum + Math.pow(acc - avgAcc, 2), 0) / data.accs.length
        : 0.1;
      const weightedDistance = distanceToOptimal * (1 + variance);

      if (weightedDistance < closestToOptimal) {
        closestToOptimal = weightedDistance;
        bestLevel = parseInt(level);
      }

      // Strong signals override subtle optimization
      if (avgAcc > FLOW_ZONE_MAX && data.accs.length >= 3) {
        bestLevel = Math.min(4, parseInt(level) + 1);
      }
      if (avgAcc < FLOW_ZONE_MIN - 0.15 && data.accs.length >= 2) {
        bestLevel = Math.max(1, parseInt(level) - 1);
      }
    });

    return bestLevel;
  },

  // Determine if level should adjust after session (flow state aware)
  shouldAdjustLevel: (accuracy, currentLevel, reactionTimes = []) => {
    // Factor in reaction time consistency
    let rtFactor = 0;
    if (reactionTimes.length >= 5) {
      const avgRT = reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length;
      const rtVariance = reactionTimes.reduce((sum, rt) => sum + Math.pow(rt - avgRT, 2), 0) / reactionTimes.length;
      // Low variance + fast RT = confident player
      if (rtVariance < 100000 && avgRT < 1500) rtFactor = 0.05;
    }

    const adjustedThresholdUp = FLOW_ZONE_MAX - rtFactor;
    const adjustedThresholdDown = FLOW_ZONE_MIN - 0.15 + rtFactor;

    if (accuracy >= adjustedThresholdUp && currentLevel < 4) return 1;  // Move up
    if (accuracy < adjustedThresholdDown && currentLevel > 1) return -1;  // Move down
    return 0; // Stay in flow zone
  },

  // Detect flow state: boredom (too easy) vs anxiety (too hard) vs flow (just right)
  detectFlowState: (accuracy, reactionTimes = []) => {
    const avgRT = reactionTimes.length > 0
      ? reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length
      : 1500;

    if (accuracy >= FLOW_ZONE_MAX && avgRT < 1200) {
      return 'boredom'; // Too easy - fast and accurate
    } else if (accuracy < FLOW_ZONE_MIN && avgRT > 2000) {
      return 'anxiety'; // Too hard - slow and inaccurate
    } else if (accuracy >= FLOW_ZONE_MIN && accuracy <= FLOW_ZONE_MAX) {
      return 'flow'; // Just right
    }
    return 'learning'; // Outside flow but not extreme
  },
};

// ============================================================================
// AUDIO ENGINE - Pre-recorded Local Audio Files
// ============================================================================
const createAudioEngine = () => {
  let ctx = null;
  const letters = ['C', 'H', 'K', 'L', 'Q', 'R', 'S', 'T'];
  const audioCache = new Map();
  let currentAudio = null;
  let preloaded = false;

  const getContext = () => {
    if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
    return ctx;
  };

  // Preload all audio files
  const preload = async () => {
    const promises = letters.map(async (letter) => {
      try {
        const audio = new Audio(`/audio/${letter}.mp3`);
        audio.preload = 'auto';
        await new Promise((resolve, reject) => {
          audio.oncanplaythrough = resolve;
          audio.onerror = reject;
          setTimeout(resolve, 2000);
        });
        audioCache.set(letter, `/audio/${letter}.mp3`);
      } catch (e) {
        console.warn(`Failed to preload ${letter}.mp3`);
      }
    });

    await Promise.all(promises);
    preloaded = audioCache.size === letters.length;
    return { success: preloaded, cached: audioCache.size };
  };

  // Play a letter sound
  const speak = (letter) => {
    const upperLetter = letter.toUpperCase();

    // Stop any currently playing audio
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      currentAudio = null;
    }

    // Use pre-recorded audio
    const audioSrc = audioCache.get(upperLetter) || `/audio/${upperLetter}.mp3`;
    currentAudio = new Audio(audioSrc);
    currentAudio.play().catch(e => {
      console.warn('Audio playback failed:', e);
      speakWebSpeech(upperLetter);
    });
  };

  // Web Speech API fallback
  const speakWebSpeech = (letter) => {
    speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(letter);
    utterance.rate = 0.9;
    utterance.pitch = 0.8;
    speechSynthesis.speak(utterance);
  };

  const playTone = (freq, duration = 0.1) => {
    const audio = getContext();
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    osc.connect(gain);
    gain.connect(audio.destination);
    osc.frequency.value = freq;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.3, audio.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, audio.currentTime + duration);
    osc.start();
    osc.stop(audio.currentTime + duration);
  };

  const isPreloaded = () => preloaded;

  return {
    speak,
    speakWebSpeech,
    playTone,
    letters,
    getContext,
    preload,
    isPreloaded,
  };
};

// ============================================================================
// PARTICLE SYSTEM - Floating neurons background
// ============================================================================
const NeuralBackground = ({ intensity = 0.5 }) => {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let width = canvas.width = window.innerWidth;
    let height = canvas.height = window.innerHeight;

    const createParticles = () => {
      const count = Math.floor(50 * intensity);
      return Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 2 + 1,
        pulse: Math.random() * Math.PI * 2,
      }));
    };

    particlesRef.current = createParticles();

    const animate = () => {
      ctx.fillStyle = 'rgba(8, 10, 15, 0.1)';
      ctx.fillRect(0, 0, width, height);

      particlesRef.current.forEach((p, i) => {
        p.x += p.vx;
        p.y += p.vy;
        p.pulse += 0.02;

        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;

        const glow = 0.3 + Math.sin(p.pulse) * 0.2;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(120, 200, 255, ${glow})`;
        ctx.fill();

        // Connect nearby particles
        particlesRef.current.slice(i + 1).forEach(p2 => {
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(80, 160, 220, ${(1 - dist / 120) * 0.15})`;
            ctx.stroke();
          }
        });
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, [intensity]);

  return <canvas ref={canvasRef} className="neural-bg" />;
};

// ============================================================================
// COACH TIP - Subtle fading message at top of screen
// ============================================================================
const CoachTip = ({ tip, visible }) => {
  return (
    <div className={`coach-tip ${visible ? 'visible' : ''}`}>
      <Icons.Sparkles size={14} />
      <span>{tip}</span>
    </div>
  );
};

// ============================================================================
// INTERACTIVE TUTORIAL - Onboarding with training wheels
// Research shows good onboarding increases retention by 50%
// ============================================================================
const Tutorial = ({ onComplete, audioEngine }) => {
  const [step, setStep] = useState(0);
  const [tutorialState, setTutorialState] = useState('intro'); // intro, watching, practicing, done
  const [demoPosition, setDemoPosition] = useState(null);
  const [demoLetter, setDemoLetter] = useState('');
  const [demoHistory, setDemoHistory] = useState({ positions: [], letters: [] });
  const [userResponse, setUserResponse] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [practiceRound, setPracticeRound] = useState(0);
  const [practiceScore, setPracticeScore] = useState({ correct: 0, total: 0 });
  const demoInterval = useRef(null);

  const tutorialSteps = [
    {
      title: "Welcome to Dual N-Back",
      content: "This game trains your working memory - the mental notepad you use every day.",
      expectation: "You'll start around 30-40% accuracy. That's completely normal!",
      icon: <Icons.Brain size={48} />,
    },
    {
      title: "Two Things to Track",
      content: "Each round shows a POSITION on the grid and plays a LETTER sound. Track both!",
      icon: <Icons.Eye size={32} />,
      iconSecond: <Icons.Volume size={32} />,
    },
    {
      title: "The N-Back Rule",
      content: "In 1-Back mode, compare to what happened 1 step ago. Was the position the same? Was the letter the same?",
      highlight: "We'll start with 1-Back to learn the basics.",
    },
    {
      title: "Watch a Demo",
      content: "Let's see a few rounds in action. Watch the position and listen to the letter.",
      action: 'demo',
    },
    {
      title: "Now You Try!",
      content: "Press A (or left button) when POSITION matches. Press L (or right button) when SOUND matches.",
      action: 'practice',
    },
  ];

  // Demo sequence for step 3
  const runDemo = useCallback(() => {
    const sequence = [
      { pos: 4, letter: 'C' },
      { pos: 2, letter: 'H' },
      { pos: 4, letter: 'H' }, // Position match AND audio match!
      { pos: 7, letter: 'K' },
      { pos: 7, letter: 'L' }, // Position match
    ];

    let i = 0;
    setDemoHistory({ positions: [], letters: [] });

    const playStep = () => {
      if (i >= sequence.length) {
        clearInterval(demoInterval.current);
        setTimeout(() => setStep(4), 1500);
        return;
      }

      const { pos, letter } = sequence[i];
      setDemoPosition(pos);
      setDemoLetter(letter);
      setDemoHistory(prev => ({
        positions: [...prev.positions, pos],
        letters: [...prev.letters, letter],
      }));

      // Highlight matches
      if (i > 0) {
        const prevPos = sequence[i - 1].pos;
        const prevLetter = sequence[i - 1].letter;
        if (pos === prevPos || letter === prevLetter) {
          setShowHint(true);
          setTimeout(() => setShowHint(false), 2000);
        }
      }

      audioEngine.current?.speak(letter);
      i++;
    };

    playStep();
    demoInterval.current = setInterval(playStep, 2500);
  }, [audioEngine]);

  // Practice mode
  const practiceSequence = useRef([
    { pos: 4, letter: 'C', posMatch: false, audioMatch: false },
    { pos: 2, letter: 'H', posMatch: false, audioMatch: false },
    { pos: 2, letter: 'K', posMatch: true, audioMatch: false },
    { pos: 5, letter: 'K', posMatch: false, audioMatch: true },
    { pos: 5, letter: 'L', posMatch: true, audioMatch: false },
    { pos: 8, letter: 'L', posMatch: false, audioMatch: true },
  ]);

  const runPractice = useCallback(() => {
    setPracticeRound(0);
    setPracticeScore({ correct: 0, total: 0 });
    setUserResponse(null);
    setTutorialState('practicing');

    const playPracticeStep = (round) => {
      if (round >= practiceSequence.current.length) {
        setTutorialState('done');
        return;
      }

      const { pos, letter } = practiceSequence.current[round];
      setDemoPosition(pos);
      setDemoLetter(letter);
      setDemoHistory(prev => ({
        positions: [...prev.positions.slice(-1), pos],
        letters: [...prev.letters.slice(-1), letter],
      }));
      setUserResponse(null);
      setShowHint(false);

      audioEngine.current?.speak(letter);
      setPracticeRound(round);

      // Show hint after 2 seconds if user hasn't responded
      if (round > 0) {
        setTimeout(() => {
          setShowHint(true);
        }, 2500);
      }
    };

    setDemoHistory({ positions: [], letters: [] });
    playPracticeStep(0);
  }, [audioEngine]);

  const handlePracticeResponse = (type) => {
    if (practiceRound === 0) return; // Can't respond to first item

    const current = practiceSequence.current[practiceRound];
    const isCorrect = (type === 'position' && current.posMatch) ||
                      (type === 'audio' && current.audioMatch);

    setUserResponse({ type, correct: isCorrect });

    if (isCorrect) {
      audioEngine.current?.playTone(880, 0.1);
      setPracticeScore(prev => ({ correct: prev.correct + 1, total: prev.total + 1 }));
    } else {
      audioEngine.current?.playTone(220, 0.2);
      setPracticeScore(prev => ({ ...prev, total: prev.total + 1 }));
    }

    // Move to next after delay
    setTimeout(() => {
      const nextRound = practiceRound + 1;
      if (nextRound >= practiceSequence.current.length) {
        setTutorialState('done');
      } else {
        const { pos, letter } = practiceSequence.current[nextRound];
        setDemoPosition(pos);
        setDemoLetter(letter);
        setDemoHistory(prev => ({
          positions: [...prev.positions.slice(-1), pos],
          letters: [...prev.letters.slice(-1), letter],
        }));
        setUserResponse(null);
        setShowHint(false);
        audioEngine.current?.speak(letter);
        setPracticeRound(nextRound);

        // Show hint after delay
        setTimeout(() => setShowHint(true), 2500);
      }
    }, 1500);
  };

  // Keyboard handler for practice
  useEffect(() => {
    if (tutorialState !== 'practicing') return;

    const handleKey = (e) => {
      if (e.key.toLowerCase() === 'a') handlePracticeResponse('position');
      if (e.key.toLowerCase() === 'l') handlePracticeResponse('audio');
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [tutorialState, practiceRound]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (demoInterval.current) clearInterval(demoInterval.current);
    };
  }, []);

  const currentStep = tutorialSteps[step];

  const handleNext = () => {
    if (step === 3) {
      setTutorialState('watching');
      runDemo();
    } else if (step === 4) {
      runPractice();
    } else if (step < tutorialSteps.length - 1) {
      setStep(step + 1);
    }
  };

  const handleComplete = () => {
    setTutorialComplete();
    onComplete();
  };

  // Tutorial done screen
  if (tutorialState === 'done') {
    return (
      <div className="tutorial-container">
        <div className="tutorial-complete">
          <div className="tutorial-icon success">
            <Icons.Trophy size={48} />
          </div>
          <h2>You've Got It!</h2>
          <p className="tutorial-score">
            Practice score: {practiceScore.correct}/{practiceScore.total}
          </p>
          <p>
            Remember: 30-40% accuracy is normal when starting. The game adapts to keep you at the perfect challenge level.
          </p>
          <div className="tutorial-tip">
            <Icons.Lightbulb size={16} />
            <span>Pro tip: Trust your gut. Overthinking slows you down.</span>
          </div>
          <button className="tutorial-btn primary" onClick={handleComplete}>
            Start Training
            <Icons.ChevronRight size={18} />
          </button>
        </div>
      </div>
    );
  }

  // Demo/Practice view
  if (tutorialState === 'watching' || tutorialState === 'practicing') {
    const isPractice = tutorialState === 'practicing';
    const currentItem = isPractice ? practiceSequence.current[practiceRound] : null;

    return (
      <div className="tutorial-container">
        <div className="tutorial-header">
          <span className="tutorial-badge">
            {isPractice ? 'Practice Mode (1-Back)' : 'Demo (1-Back)'}
          </span>
          {isPractice && (
            <span className="practice-progress">
              Round {practiceRound + 1}/{practiceSequence.current.length}
            </span>
          )}
        </div>

        <div className="tutorial-game-area">
          <TutorialGrid
            activePosition={demoPosition}
            history={demoHistory.positions}
          />

          <div className="tutorial-letter">{demoLetter}</div>

          {/* Match hints */}
          {showHint && !isPractice && demoHistory.positions.length > 1 && (
            <div className="tutorial-hint">
              {demoHistory.positions[demoHistory.positions.length - 1] === demoHistory.positions[demoHistory.positions.length - 2] && (
                <span className="hint-tag position">Position Match! (Press A)</span>
              )}
              {demoHistory.letters[demoHistory.letters.length - 1] === demoHistory.letters[demoHistory.letters.length - 2] && (
                <span className="hint-tag audio">Audio Match! (Press L)</span>
              )}
            </div>
          )}

          {/* Practice hints */}
          {isPractice && practiceRound > 0 && showHint && !userResponse && (
            <div className="tutorial-hint">
              {currentItem?.posMatch && <span className="hint-tag position">Position matches! Press A</span>}
              {currentItem?.audioMatch && <span className="hint-tag audio">Sound matches! Press L</span>}
              {!currentItem?.posMatch && !currentItem?.audioMatch && <span className="hint-tag neutral">No match - wait</span>}
            </div>
          )}

          {/* User response feedback */}
          {userResponse && (
            <div className={`tutorial-feedback ${userResponse.correct ? 'correct' : 'incorrect'}`}>
              {userResponse.correct ? 'Correct!' : 'Not quite - keep trying!'}
            </div>
          )}

          {isPractice && practiceRound > 0 && (
            <div className="practice-buttons">
              <button
                className={`practice-btn position ${userResponse?.type === 'position' ? (userResponse.correct ? 'correct' : 'incorrect') : ''}`}
                onClick={() => handlePracticeResponse('position')}
                disabled={!!userResponse}
              >
                <span>Position</span>
                <kbd>A</kbd>
              </button>
              <button
                className={`practice-btn audio ${userResponse?.type === 'audio' ? (userResponse.correct ? 'correct' : 'incorrect') : ''}`}
                onClick={() => handlePracticeResponse('audio')}
                disabled={!!userResponse}
              >
                <span>Sound</span>
                <kbd>L</kbd>
              </button>
            </div>
          )}

          {isPractice && practiceRound === 0 && (
            <p className="practice-wait">Watch and remember this first one...</p>
          )}
        </div>
      </div>
    );
  }

  // Standard tutorial steps
  return (
    <div className="tutorial-container">
      <div className="tutorial-progress">
        {tutorialSteps.map((_, i) => (
          <div key={i} className={`progress-dot ${i === step ? 'active' : i < step ? 'done' : ''}`} />
        ))}
      </div>

      <div className="tutorial-content">
        <div className="tutorial-icon">
          {currentStep.icon}
          {currentStep.iconSecond && (
            <>
              <span className="icon-plus">+</span>
              {currentStep.iconSecond}
            </>
          )}
        </div>

        <h2>{currentStep.title}</h2>
        <p>{currentStep.content}</p>

        {currentStep.expectation && (
          <div className="tutorial-expectation">
            <Icons.Zap size={16} />
            <span>{currentStep.expectation}</span>
          </div>
        )}

        {currentStep.highlight && (
          <div className="tutorial-highlight">
            {currentStep.highlight}
          </div>
        )}
      </div>

      <div className="tutorial-actions">
        {step > 0 && (
          <button className="tutorial-btn secondary" onClick={() => setStep(step - 1)}>
            Back
          </button>
        )}
        <button className="tutorial-btn primary" onClick={handleNext}>
          {step === tutorialSteps.length - 1 ? 'Try It' : step === 3 ? 'Watch Demo' : 'Next'}
          <Icons.ChevronRight size={18} />
        </button>
      </div>

      <button className="tutorial-skip" onClick={handleComplete}>
        Skip tutorial
      </button>
    </div>
  );
};

// Simplified grid for tutorial
const TutorialGrid = ({ activePosition, history }) => {
  const positions = [
    { x: 50, y: 20 }, { x: 20, y: 35 }, { x: 80, y: 35 },
    { x: 35, y: 50 }, { x: 50, y: 50 }, { x: 65, y: 50 },
    { x: 20, y: 65 }, { x: 80, y: 65 }, { x: 50, y: 80 },
  ];

  const previousPos = history.length > 1 ? history[history.length - 2] : null;

  return (
    <div className="tutorial-grid">
      {positions.map((pos, i) => (
        <div
          key={i}
          className={`tutorial-node ${activePosition === i ? 'active' : ''} ${previousPos === i ? 'previous' : ''}`}
          style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
        />
      ))}
    </div>
  );
};

// ============================================================================
// ORGANIC GRID - Circular node layout
// ============================================================================
const OrganicGrid = ({ activePosition, trail }) => {
  const positions = [
    { x: 50, y: 20 },   // 0: top center
    { x: 20, y: 35 },   // 1: top left
    { x: 80, y: 35 },   // 2: top right
    { x: 35, y: 50 },   // 3: mid left
    { x: 50, y: 50 },   // 4: center
    { x: 65, y: 50 },   // 5: mid right
    { x: 20, y: 65 },   // 6: bottom left
    { x: 80, y: 65 },   // 7: bottom right
    { x: 50, y: 80 },   // 8: bottom center
  ];

  return (
    <div className="organic-grid">
      <svg className="trail-svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
        {trail.length > 1 && trail.slice(0, -1).map((pos, i) => {
          const from = positions[pos];
          const to = positions[trail[i + 1]];
          const opacity = (i + 1) / trail.length * 0.6;
          return (
            <line
              key={i}
              x1={from.x} y1={from.y}
              x2={to.x} y2={to.y}
              stroke={`rgba(100, 200, 255, ${opacity})`}
              strokeWidth="0.8"
              strokeLinecap="round"
            />
          );
        })}
      </svg>

      {positions.map((pos, i) => (
        <div
          key={i}
          className={`node ${activePosition === i ? 'active' : ''} ${trail.includes(i) ? 'in-trail' : ''}`}
          style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
        >
          <div className="node-core" />
          <div className="node-ring" />
          <div className="node-pulse" />
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// RESPONSE BUTTONS - Minimal, elegant
// ============================================================================
const ResponseButtons = ({ onResponse, disabled, feedback }) => (
  <div className="response-area">
    <button
      className={`response-btn position-btn ${feedback?.position}`}
      aria-label="Position match (keyboard: A)"
      onClick={() => onResponse('position')}
      disabled={disabled}
    >
      <span className="btn-icon">◉</span>
      <span className="btn-label">Position</span>
      <span className="btn-hint">A</span>
    </button>
    <button
      className={`response-btn audio-btn ${feedback?.audio}`}
      aria-label="Audio match (keyboard: L)"
      onClick={() => onResponse('audio')}
      disabled={disabled}
    >
      <span className="btn-icon">♫</span>
      <span className="btn-label">Sound</span>
      <span className="btn-hint">L</span>
    </button>
  </div>
);

// ============================================================================
// STATS DISPLAY - Brain scan aesthetic
// ============================================================================
const StatsDisplay = ({ stats, nLevel, isExpanded, onToggle }) => {
  const accuracy = stats.total > 0
    ? Math.round((stats.correct / stats.total) * 100)
    : 0;

  return (
    <div className={`stats-panel ${isExpanded ? 'expanded' : ''}`}>
      <button className="stats-toggle" onClick={onToggle}>
        <span className="toggle-icon">{isExpanded ? '▼' : '▲'}</span>
        <span className="n-badge">{nLevel}-Back</span>
        <span className="accuracy-mini">{accuracy}%</span>
      </button>

      {isExpanded && (
        <div className="stats-content">
          <div className="stat-row">
            <span className="stat-label">Position</span>
            <div className="stat-bar">
              <div
                className="stat-fill position"
                style={{ width: `${stats.positionTotal > 0 ? (stats.positionCorrect / stats.positionTotal) * 100 : 0}%` }}
              />
            </div>
            <span className="stat-value">{stats.positionTotal > 0 ? Math.round((stats.positionCorrect / stats.positionTotal) * 100) : 0}%</span>
          </div>
          <div className="stat-row">
            <span className="stat-label">Audio</span>
            <div className="stat-bar">
              <div
                className="stat-fill audio"
                style={{ width: `${stats.audioTotal > 0 ? (stats.audioCorrect / stats.audioTotal) * 100 : 0}%` }}
              />
            </div>
            <span className="stat-value">{stats.audioTotal > 0 ? Math.round((stats.audioCorrect / stats.audioTotal) * 100) : 0}%</span>
          </div>
          <div className="stat-row">
            <span className="stat-label">Trial</span>
            <span className="stat-value">{stats.trial} / 20</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// LANDING PAGE - Minimal onboarding
// ============================================================================
const LandingPage = ({ settings, onUpdateSettings, onStart, audioReady }) => {
  const [activeTab, setActiveTab] = useState('intro');

  return (
    <div className="landing-page">
      <div className="landing-header">
        <h1 className="landing-title">Neural Sync</h1>
        <p className="landing-tagline">Dual N-Back Cognitive Training</p>
      </div>

      <div className="landing-tabs">
        <button
          className={`tab ${activeTab === 'intro' ? 'active' : ''}`}
          onClick={() => setActiveTab('intro')}
        >
          What is this?
        </button>
        <button
          className={`tab ${activeTab === 'rules' ? 'active' : ''}`}
          onClick={() => setActiveTab('rules')}
        >
          How to Play
        </button>
        <button
          className={`tab ${activeTab === 'benefits' ? 'active' : ''}`}
          onClick={() => setActiveTab('benefits')}
        >
          Benefits
        </button>
      </div>

      <div className="landing-content">
        {activeTab === 'intro' && (
          <div className="tab-content intro-content">
            <div className="intro-visual">
              <div className="brain-icon"><Icons.Brain size={64} /></div>
            </div>
            <h2>Train Your Working Memory</h2>
            <p>
              Dual N-Back is one of the few cognitive training exercises with scientific
              evidence suggesting it may improve working memory and potentially
              fluid intelligence.
            </p>
            <p>
              You'll track two independent sequences simultaneously — <strong>visual positions</strong> on
              a grid and <strong>audio letters</strong> — and identify when either matches
              what appeared <em>n steps back</em>.
            </p>
            <div className="complexity-note">
              <span className="note-icon"><Icons.Zap size={18} /></span>
              <span>It's challenging by design. Difficulty adapts to your performance.</span>
            </div>
          </div>
        )}

        {activeTab === 'rules' && (
          <div className="tab-content rules-content">
            <h2>How It Works</h2>

            <div className="rule-block">
              <div className="rule-number">1</div>
              <div className="rule-text">
                <strong>Watch & Listen</strong>
                <p>Each trial shows a position on the grid AND plays a letter sound.</p>
              </div>
            </div>

            <div className="rule-block">
              <div className="rule-number">2</div>
              <div className="rule-text">
                <strong>Remember N Steps Back</strong>
                <p>In 2-back mode, compare to what happened 2 trials ago. In 3-back, 3 trials ago, etc.</p>
              </div>
            </div>

            <div className="rule-block">
              <div className="rule-number">3</div>
              <div className="rule-text">
                <strong>Respond When There's a Match</strong>
                <p>Press <kbd>A</kbd> (or left button) if <em>position</em> matches.</p>
                <p>Press <kbd>L</kbd> (or right button) if <em>sound</em> matches.</p>
                <p>Both can match simultaneously — press both!</p>
              </div>
            </div>

            <div className="rule-block">
              <div className="rule-number">4</div>
              <div className="rule-text">
                <strong>Don't Press If No Match</strong>
                <p>Only respond when you detect a match. Silence = no match detected.</p>
              </div>
            </div>

            <div className="pro-tip">
              <span className="tip-icon"><Icons.Lightbulb size={18} /></span>
              <span>Pro tip: Don't overthink it. Trust your intuition as you build the skill.</span>
            </div>
          </div>
        )}

        {activeTab === 'benefits' && (
          <div className="tab-content benefits-content">
            <h2>Why Practice?</h2>

            <div className="benefit-section">
              <h3><Icons.Target size={18} className="section-icon" /> Short-Term (Days to Weeks)</h3>
              <ul>
                <li>Improved focus and concentration during sessions</li>
                <li>Better at holding multiple items in mind</li>
                <li>Enhanced pattern recognition</li>
                <li>Increased mental stamina for demanding tasks</li>
              </ul>
            </div>

            <div className="benefit-section">
              <h3><Icons.TrendingUp size={18} className="section-icon" /> Long-Term (Months of Practice)</h3>
              <ul>
                <li>Research suggests potential gains in fluid intelligence (Gf)</li>
                <li>Improved working memory capacity</li>
                <li>Better executive function and cognitive control</li>
                <li>Some studies show transfer to untrained tasks</li>
              </ul>
            </div>

            <div className="research-note">
              <h4><Icons.BookOpen size={16} className="section-icon" /> The Research</h4>
              <p>
                The landmark 2008 Jaeggi study showed IQ gains after dual n-back training.
                Results have been mixed in replications, but meta-analyses suggest
                modest but real benefits for working memory, with some evidence
                for transfer to fluid reasoning.
              </p>
              <p className="caveat">
                Consistency matters more than intensity. 15-20 minutes daily beats
                occasional hour-long sessions.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="audio-status-landing">
        {audioReady ? (
          <><Icons.Check size={14} /> Audio ready</>
        ) : (
          <><Icons.Loader size={14} className="spin" /> Loading...</>
        )}
      </div>

      <button className="start-training-btn" onClick={onStart} aria-label="Start training session">
        <span className="btn-glow" />
        <span className="btn-text">Begin Training</span>
      </button>
    </div>
  );
};

// ============================================================================
// ANALYTICS DASHBOARD - Session history, trends, and insights
// ============================================================================
const AnalyticsDashboard = ({ isOpen, onClose, sessions, streak }) => {
  if (!isOpen) return null;

  // Calculate statistics
  const last7Days = sessions.filter(s => {
    const sessionDate = new Date(s.timestamp);
    const weekAgo = new Date(Date.now() - 7 * 86400000);
    return sessionDate >= weekAgo;
  });

  const totalSessions = sessions.length;
  const avgAccuracy = sessions.length > 0
    ? Math.round(sessions.reduce((sum, s) => sum + (s.accuracy || 0), 0) / sessions.length)
    : 0;
  const highestLevel = sessions.length > 0
    ? Math.max(...sessions.map(s => s.nLevel || 1))
    : 1;
  const avgReactionTime = sessions.filter(s => s.avgReactionTime).length > 0
    ? Math.round(sessions.filter(s => s.avgReactionTime).reduce((sum, s) => sum + s.avgReactionTime, 0) / sessions.filter(s => s.avgReactionTime).length)
    : null;

  // Get last 10 sessions for trend chart
  const recentSessions = sessions.slice(-10);

  // Calculate daily practice heatmap (last 30 days)
  const last30Days = [];
  for (let i = 29; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const dateStr = date.toDateString();
    const sessionsOnDay = sessions.filter(s => new Date(s.timestamp).toDateString() === dateStr);
    last30Days.push({
      date: dateStr,
      count: sessionsOnDay.length,
      avgAcc: sessionsOnDay.length > 0
        ? Math.round(sessionsOnDay.reduce((sum, s) => sum + (s.accuracy || 0), 0) / sessionsOnDay.length)
        : 0,
    });
  }

  return (
    <div className="analytics-overlay" onClick={onClose}>
      <div className="analytics-panel" onClick={e => e.stopPropagation()}>
        <div className="analytics-header">
          <h3>Your Progress</h3>
          <button className="close-btn" onClick={onClose} aria-label="Close analytics">×</button>
        </div>

        {/* Summary Stats */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{totalSessions}</div>
            <div className="stat-label">Sessions</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{avgAccuracy}%</div>
            <div className="stat-label">Avg Accuracy</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{highestLevel}-Back</div>
            <div className="stat-label">Highest Level</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{streak.current}</div>
            <div className="stat-label">Day Streak</div>
          </div>
        </div>

        {/* Accuracy Trend */}
        {recentSessions.length > 0 && (
          <div className="trend-section">
            <h4>Recent Accuracy Trend</h4>
            <div className="trend-chart">
              {recentSessions.map((s, i) => (
                <div
                  key={i}
                  className="trend-bar"
                  style={{ height: `${s.accuracy || 0}%` }}
                  title={`${s.accuracy || 0}% at ${s.nLevel}-Back`}
                >
                  <span className="trend-label">{s.accuracy || 0}%</span>
                </div>
              ))}
            </div>
            <div className="trend-axis">
              <span>Older</span>
              <span>Recent</span>
            </div>
          </div>
        )}

        {/* Activity Heatmap */}
        <div className="heatmap-section">
          <h4>Activity (Last 30 Days)</h4>
          <div className="heatmap">
            {last30Days.map((day, i) => (
              <div
                key={i}
                className={`heatmap-cell ${day.count > 0 ? 'active' : ''}`}
                style={{
                  opacity: day.count > 0 ? Math.min(0.3 + day.count * 0.2, 1) : 0.1,
                }}
                title={`${new Date(day.date).toLocaleDateString()}: ${day.count} session${day.count !== 1 ? 's' : ''}${day.count > 0 ? ` (${day.avgAcc}%)` : ''}`}
              />
            ))}
          </div>
          <div className="heatmap-legend">
            <span>Less</span>
            <div className="legend-cells">
              {[0.1, 0.3, 0.5, 0.7, 1].map((opacity, i) => (
                <div key={i} className="legend-cell" style={{ opacity }} />
              ))}
            </div>
            <span>More</span>
          </div>
        </div>

        {avgReactionTime && (
          <div className="insight-card">
            <Icons.Zap size={16} />
            <span>Average reaction time: {avgReactionTime}ms</span>
          </div>
        )}

        {sessions.length === 0 && (
          <div className="empty-state">
            <Icons.BarChart size={48} />
            <p>Complete your first session to see your progress!</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// SETTINGS PANEL - Minimal, essential options only
// ============================================================================
const SettingsPanel = ({ isOpen, onClose, settings, onUpdate }) => {
  if (!isOpen) return null;

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-panel" onClick={e => e.stopPropagation()}>
        <h3>Settings</h3>

        <div className="setting-group">
          <label>N-Level</label>
          <div className="setting-buttons">
            {[1, 2, 3, 4].map(n => (
              <button
                key={n}
                className={settings.nLevel === n ? 'active' : ''}
                onClick={() => onUpdate({ ...settings, nLevel: n })}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        <div className="setting-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={settings.aiCoach}
              onChange={e => onUpdate({ ...settings, aiCoach: e.target.checked })}
            />
            <span>AI Coach</span>
          </label>
          <p className="setting-hint">Adapts difficulty and provides tips</p>
        </div>

        <div className="setting-group">
          <label>Theme</label>
          <div className="theme-selector">
            {Object.entries(THEMES).map(([key, t]) => (
              <button
                key={key}
                className={`theme-btn ${settings.theme === key ? 'active' : ''}`}
                onClick={() => onUpdate({ ...settings, theme: key })}
                style={{
                  '--theme-color': `rgb(${t.primary})`,
                  '--theme-bg': `rgb(${t.background})`,
                }}
              >
                <span className="theme-preview" />
                <span className="theme-name">{t.name}</span>
              </button>
            ))}
          </div>
        </div>

        <button className="close-settings" onClick={onClose} aria-label="Close settings">Done</button>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN APP
// ============================================================================
// ERROR BOUNDARY - Graceful error handling
// ============================================================================
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Neural Sync Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, rgb(8, 12, 24) 0%, rgb(15, 20, 35) 100%)',
          color: 'rgba(255, 255, 255, 0.9)',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <Icons.Brain size={64} style={{ marginBottom: '1.5rem', opacity: 0.6 }} />
          <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem', fontWeight: 500 }}>
            Something went wrong
          </h1>
          <p style={{ opacity: 0.7, marginBottom: '2rem', maxWidth: '400px' }}>
            Neural Sync encountered an unexpected error. Your progress has been saved.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '0.75rem 2rem',
              background: 'rgba(100, 200, 255, 0.15)',
              border: '1px solid rgba(100, 200, 255, 0.3)',
              borderRadius: '8px',
              color: 'rgb(100, 200, 255)',
              cursor: 'pointer',
              fontSize: '1rem',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.background = 'rgba(100, 200, 255, 0.25)'}
            onMouseOut={(e) => e.target.style.background = 'rgba(100, 200, 255, 0.15)'}
          >
            Reload App
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// ============================================================================
export default function DualNBackApp() {
  const [gameState, setGameState] = useState('idle'); // idle, tutorial, playing, paused, complete
  const [showTutorial, setShowTutorial] = useState(() => !isTutorialComplete());
  const [settings, setSettings] = useState(() => {
    const saved = loadSettings();
    return saved || {
      nLevel: 2,
      interval: 2500,
      trials: 20,
      aiCoach: true, // Simple toggle - that's it
      theme: 'neural', // Visual theme
    };
  });

  // Get current theme colors
  const theme = THEMES[settings.theme] || THEMES.neural;
  const [currentPosition, setCurrentPosition] = useState(null);
  const [currentLetter, setCurrentLetter] = useState('');
  const [positionHistory, setPositionHistory] = useState([]);
  const [letterHistory, setLetterHistory] = useState([]);
  const [stats, setStats] = useState({
    correct: 0, total: 0, trial: 0,
    positionCorrect: 0, positionTotal: 0,
    audioCorrect: 0, audioTotal: 0,
  });
  const [feedback, setFeedback] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [responded, setResponded] = useState({ position: false, audio: false });
  const [audioReady, setAudioReady] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const isMutedRef = useRef(false);
  const [sessions, setSessions] = useState([]);
  const [coachTip, setCoachTip] = useState('');
  const [showCoachTip, setShowCoachTip] = useState(false);
  const [sessionFeedback, setSessionFeedback] = useState('');
  const [streak, setStreak] = useState(() => getStreak());
  const [reactionTimes, setReactionTimes] = useState([]);

  const audioEngine = useRef(createAudioEngine());
  const gameLoop = useRef(null);
  const runTrialRef = useRef(null);
  const trialRef = useRef(0);
  const coachTipTimeout = useRef(null);
  const trialStartTime = useRef(null);

  // Sync muted ref with state
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  // Save settings when they change
  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  // Load session history on mount
  useEffect(() => {
    const loadedSessions = getSessions();
    setSessions(loadedSessions);

    // If AI Coach is enabled, set optimal level based on history
    if (settings.aiCoach && loadedSessions.length >= 3) {
      const optimalLevel = AICoach.getOptimalLevel(loadedSessions);
      if (optimalLevel !== settings.nLevel) {
        setSettings(prev => ({ ...prev, nLevel: optimalLevel }));
      }
    }
  }, []);

  // Preload audio on mount
  useEffect(() => {
    audioEngine.current.preload().then(result => {
      setAudioReady(result.success);
    });
  }, []);

  // Show coach tip with fade animation
  const showTip = useCallback((tip) => {
    if (!tip || !settings.aiCoach) return;

    // Clear any existing timeout
    if (coachTipTimeout.current) {
      clearTimeout(coachTipTimeout.current);
    }

    setCoachTip(tip);
    setShowCoachTip(true);

    // Fade out after 4 seconds
    coachTipTimeout.current = setTimeout(() => {
      setShowCoachTip(false);
    }, 4000);
  }, [settings.aiCoach]);

  // Generate next stimulus
  const generateStimulus = useCallback(() => {
    const position = Math.floor(Math.random() * 9);
    const letter = audioEngine.current.letters[Math.floor(Math.random() * 8)];
    return { position, letter };
  }, []);

  // Check if current matches n-back
  const checkMatch = useCallback((type) => {
    const n = settings.nLevel;
    if (type === 'position') {
      if (positionHistory.length < n) return false;
      return positionHistory[positionHistory.length - n] === currentPosition;
    } else {
      if (letterHistory.length < n) return false;
      return letterHistory[letterHistory.length - n] === currentLetter;
    }
  }, [settings.nLevel, positionHistory, letterHistory, currentPosition, currentLetter]);

  // Handle user response
  const handleResponse = useCallback((type) => {
    if (responded[type] || gameState !== 'playing') return;

    // Track reaction time
    if (trialStartTime.current) {
      const rt = Date.now() - trialStartTime.current;
      setReactionTimes(prev => [...prev, rt]);
    }

    const isMatch = checkMatch(type);
    const isCorrect = isMatch;

    setResponded(prev => ({ ...prev, [type]: true }));
    setFeedback(prev => ({ ...prev, [type]: isCorrect ? 'correct' : 'incorrect' }));

    if (isCorrect) {
      audioEngine.current.playTone(880, 0.1);
    } else {
      audioEngine.current.playTone(220, 0.2);
    }

    setStats(prev => ({
      ...prev,
      correct: prev.correct + (isCorrect ? 1 : 0),
      total: prev.total + 1,
      [`${type}Correct`]: prev[`${type}Correct`] + (isCorrect ? 1 : 0),
      [`${type}Total`]: prev[`${type}Total`] + 1,
    }));
  }, [responded, gameState, checkMatch]);

  // Run one trial
  const runTrial = useCallback(() => {
    // Evaluate non-responses from previous trial
    if (trialRef.current > 0) {
      const n = settings.nLevel;

      if (!responded.position && positionHistory.length >= n) {
        const wasMatch = positionHistory[positionHistory.length - n] === currentPosition;
        if (wasMatch) {
          setStats(prev => ({
            ...prev,
            total: prev.total + 1,
            positionTotal: prev.positionTotal + 1,
          }));
        }
      }

      if (!responded.audio && letterHistory.length >= n) {
        const wasMatch = letterHistory[letterHistory.length - n] === currentLetter;
        if (wasMatch) {
          setStats(prev => ({
            ...prev,
            total: prev.total + 1,
            audioTotal: prev.audioTotal + 1,
          }));
        }
      }
    }

    trialRef.current += 1;

    if (trialRef.current > settings.trials) {
      setGameState('complete');
      return;
    }

    const { position, letter } = generateStimulus();

    setCurrentPosition(position);
    setCurrentLetter(letter);
    setPositionHistory(prev => [...prev, position]);
    setLetterHistory(prev => [...prev, letter]);
    setFeedback(null);
    setResponded({ position: false, audio: false });
    setStats(prev => ({ ...prev, trial: trialRef.current }));

    // Track trial start time for reaction time measurement
    trialStartTime.current = Date.now();

    if (!isMutedRef.current) setTimeout(() => audioEngine.current.speak(letter), 100);

    // AI Coach tip (occasionally, not every trial) - now with reaction time data
    if (settings.aiCoach && trialRef.current % 5 === 0) {
      // Use a timeout to get updated stats
      setTimeout(() => {
        setStats(currentStats => {
          setReactionTimes(currentRT => {
            const tip = AICoach.getTip(currentStats, settings.nLevel, sessions, currentRT);
            if (tip) showTip(tip);
            return currentRT;
          });
          return currentStats;
        });
      }, 500);
    }
  }, [settings, generateStimulus, responded, positionHistory, letterHistory, currentPosition, currentLetter, sessions, showTip]);

  // Keep runTrialRef updated with latest runTrial
  useEffect(() => {
    runTrialRef.current = runTrial;
  }, [runTrial]);

  // Game loop - uses ref to avoid recreating interval when runTrial changes
  useEffect(() => {
    if (gameState === 'playing') {
      // Run first trial immediately
      runTrialRef.current?.();
      // Set up interval using ref to always call latest runTrial
      gameLoop.current = setInterval(() => {
        runTrialRef.current?.();
      }, settings.interval);
    }

    return () => {
      if (gameLoop.current) clearInterval(gameLoop.current);
    };
  }, [gameState, settings.interval]); // Note: runTrial NOT in deps - uses ref instead

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (gameState !== 'playing') return;
      if (e.key.toLowerCase() === 'a') handleResponse('position');
      if (e.key.toLowerCase() === 'l') handleResponse('audio');
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState, handleResponse]);

  // Handle game completion
  useEffect(() => {
    if (gameState === 'complete' && stats.total > 0) {
      const accuracy = stats.correct / stats.total;
      const avgRT = reactionTimes.length > 0
        ? reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length
        : null;

      // Save session with reaction time data
      const sessionData = {
        nLevel: settings.nLevel,
        trials: settings.trials,
        accuracy,
        positionAccuracy: stats.positionTotal > 0 ? stats.positionCorrect / stats.positionTotal : 0,
        audioAccuracy: stats.audioTotal > 0 ? stats.audioCorrect / stats.audioTotal : 0,
        interval: settings.interval,
        avgReactionTime: avgRT,
        flowState: AICoach.detectFlowState(accuracy, reactionTimes),
      };
      saveSession(sessionData);

      // Update sessions state
      const updatedSessions = getSessions();
      setSessions(updatedSessions);

      // Update streak
      const newStreak = updateStreak();
      setStreak({ current: newStreak, trainedToday: true });

      // AI Coach: Show feedback and adjust difficulty (now with reaction time)
      if (settings.aiCoach) {
        const feedback = AICoach.getSessionFeedback(stats, settings.nLevel, reactionTimes);
        setSessionFeedback(feedback);

        const adjustment = AICoach.shouldAdjustLevel(accuracy, settings.nLevel, reactionTimes);
        if (adjustment !== 0) {
          setSettings(prev => ({ ...prev, nLevel: prev.nLevel + adjustment }));
        }
      }
    }
  }, [gameState, stats, settings, reactionTimes]);

  // Start game
  const startGame = async () => {
    audioEngine.current.getContext();
    setSessionFeedback('');
    setReactionTimes([]); // Reset reaction times for new session

    setGameState('playing');
    setCurrentPosition(null);
    setCurrentLetter('');
    setPositionHistory([]);
    setLetterHistory([]);
    setStats({
      correct: 0, total: 0, trial: 0,
      positionCorrect: 0, positionTotal: 0,
      audioCorrect: 0, audioTotal: 0,
    });
    trialRef.current = 0;
  };

  // Handle tutorial completion
  const handleTutorialComplete = () => {
    setShowTutorial(false);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600&family=Space+Mono:wght@400;700&display=swap');

        :root {
          --primary: rgba(${theme.primary}, 1);
          --primary-dim: rgba(${theme.primary}, 0.6);
          --primary-glow: rgba(${theme.primary}, 0.3);
          --secondary: rgba(${theme.secondary}, 1);
          --accent: rgba(${theme.accent}, 0.9);
          --success: rgba(${theme.success}, 0.9);
          --warning: rgba(255, 200, 100, 0.9);
          --error: rgba(${theme.error}, 0.9);
          --bg-r: ${theme.background.split(',')[0].trim()};
          --bg-g: ${theme.background.split(',')[1].trim()};
          --bg-b: ${theme.background.split(',')[2].trim()};
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          overflow: hidden;
        }

        .app-container {
          min-height: 100vh;
          background: linear-gradient(135deg, rgba(${theme.background}, 1) 0%, rgba(${theme.background}, 0.9) 50%, rgba(${theme.background}, 0.95) 100%);
          color: #e0e8f0;
          font-family: 'Outfit', sans-serif;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
        }

        .neural-bg {
          position: fixed;
          inset: 0;
          z-index: 0;
          opacity: 0.7;
        }

        .game-container {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2rem;
          padding: 2rem;
          max-width: 500px;
          width: 100%;
        }

        /* Coach Tip - Fading at top */
        .coach-tip {
          position: fixed;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.6rem 1.2rem;
          background: rgba(100, 200, 255, 0.1);
          border: 1px solid rgba(100, 200, 255, 0.2);
          border-radius: 2rem;
          color: rgba(100, 200, 255, 0.9);
          font-size: 0.85rem;
          opacity: 0;
          transition: opacity 0.5s ease;
          z-index: 50;
          pointer-events: none;
        }

        .coach-tip.visible {
          opacity: 1;
        }

        .coach-tip svg {
          flex-shrink: 0;
        }

        /* Header */
        .header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 0 1rem;
        }

        .logo {
          font-family: 'Space Mono', monospace;
          font-size: 0.9rem;
          letter-spacing: 0.1em;
          color: rgba(120, 200, 255, 0.6);
          text-transform: uppercase;
        }

        .settings-btn {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.5);
          padding: 0.5rem 1rem;
          border-radius: 2rem;
          cursor: pointer;
          font-family: inherit;
          font-size: 0.85rem;
          transition: all 0.3s ease;
        }

        .settings-btn:hover {
          background: rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.8);
        }

        /* Organic Grid */
        .organic-grid {
          position: relative;
          width: 320px;
          height: 320px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(20, 30, 50, 0.8) 0%, rgba(10, 15, 25, 0.4) 70%, transparent 100%);
          box-shadow:
            0 0 60px rgba(80, 160, 220, 0.1),
            inset 0 0 60px rgba(80, 160, 220, 0.05);
        }

        .trail-svg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
        }

        .node {
          position: absolute;
          width: 48px;
          height: 48px;
          transform: translate(-50%, -50%);
          cursor: pointer;
        }

        .node-core {
          position: absolute;
          inset: 12px;
          border-radius: 50%;
          background: rgba(60, 100, 140, 0.3);
          transition: all 0.3s ease;
        }

        .node-ring {
          position: absolute;
          inset: 4px;
          border-radius: 50%;
          border: 1px solid rgba(100, 180, 240, 0.2);
          transition: all 0.3s ease;
        }

        .node-pulse {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: transparent;
          transition: all 0.3s ease;
        }

        .node.active .node-core {
          background: radial-gradient(circle, rgba(100, 200, 255, 0.9) 0%, rgba(60, 150, 220, 0.6) 100%);
          box-shadow: 0 0 20px rgba(100, 200, 255, 0.8);
        }

        .node.active .node-ring {
          border-color: rgba(100, 200, 255, 0.8);
          transform: scale(1.2);
        }

        .node.active .node-pulse {
          animation: pulse 0.6s ease-out;
        }

        .node.in-trail .node-core {
          background: rgba(80, 150, 200, 0.4);
        }

        @keyframes pulse {
          0% { transform: scale(1); opacity: 0.8; box-shadow: 0 0 0 0 rgba(100, 200, 255, 0.6); }
          100% { transform: scale(2); opacity: 0; box-shadow: 0 0 0 20px rgba(100, 200, 255, 0); }
        }

        /* Current Letter Display */
        .letter-display {
          font-family: 'Space Mono', monospace;
          font-size: 3rem;
          font-weight: 700;
          color: rgba(100, 200, 255, 0.9);
          text-shadow: 0 0 30px rgba(100, 200, 255, 0.5);
          height: 4rem;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: letterPop 0.3s ease-out;
        }

        @keyframes letterPop {
          0% { transform: scale(0.5); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }

        /* Response Buttons */
        .response-area {
          display: flex;
          gap: 1.5rem;
        }

        .response-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          padding: 1.25rem 2rem;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 1rem;
          color: rgba(255, 255, 255, 0.6);
          cursor: pointer;
          font-family: inherit;
          transition: all 0.2s ease;
          min-width: 120px;
        }

        .response-btn:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(255, 255, 255, 0.2);
          transform: translateY(-2px);
        }

        .response-btn:active:not(:disabled) {
          transform: translateY(0);
        }

        .response-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .response-btn.correct {
          background: rgba(80, 200, 120, 0.2);
          border-color: rgba(80, 200, 120, 0.5);
          color: rgba(80, 200, 120, 1);
        }

        .response-btn.incorrect {
          background: rgba(220, 80, 80, 0.2);
          border-color: rgba(220, 80, 80, 0.5);
          color: rgba(220, 80, 80, 1);
        }

        .btn-icon {
          font-size: 1.5rem;
        }

        .btn-label {
          font-size: 0.9rem;
          font-weight: 500;
        }

        .btn-hint {
          font-family: 'Space Mono', monospace;
          font-size: 0.7rem;
          opacity: 0.5;
          padding: 0.15rem 0.4rem;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 0.25rem;
        }

        /* Stats Panel */
        .stats-panel {
          position: fixed;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(15, 20, 30, 0.95);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-bottom: none;
          border-radius: 1rem 1rem 0 0;
          backdrop-filter: blur(10px);
          min-width: 280px;
          z-index: 10;
        }

        .stats-toggle {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.75rem 1.5rem;
          width: 100%;
          background: transparent;
          border: none;
          color: rgba(255, 255, 255, 0.7);
          cursor: pointer;
          font-family: inherit;
          font-size: 0.9rem;
        }

        .toggle-icon {
          font-size: 0.7rem;
          opacity: 0.5;
        }

        .n-badge {
          font-family: 'Space Mono', monospace;
          background: rgba(100, 200, 255, 0.2);
          color: rgba(100, 200, 255, 0.9);
          padding: 0.25rem 0.75rem;
          border-radius: 1rem;
          font-size: 0.8rem;
        }

        .accuracy-mini {
          margin-left: auto;
          font-family: 'Space Mono', monospace;
          color: rgba(80, 200, 120, 0.8);
        }

        .stats-content {
          padding: 0 1.5rem 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .stat-row {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .stat-label {
          font-size: 0.8rem;
          color: rgba(255, 255, 255, 0.5);
          min-width: 60px;
        }

        .stat-bar {
          flex: 1;
          height: 4px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 2px;
          overflow: hidden;
        }

        .stat-fill {
          height: 100%;
          transition: width 0.3s ease;
        }

        .stat-fill.position {
          background: linear-gradient(90deg, rgba(100, 200, 255, 0.8), rgba(150, 220, 255, 1));
        }

        .stat-fill.audio {
          background: linear-gradient(90deg, rgba(200, 150, 255, 0.8), rgba(220, 180, 255, 1));
        }

        .stat-value {
          font-family: 'Space Mono', monospace;
          font-size: 0.85rem;
          min-width: 40px;
          text-align: right;
        }

        /* Complete Screen */
        .complete-screen {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          text-align: center;
          animation: fadeIn 0.5s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .complete-title {
          font-size: 1.5rem;
          font-weight: 400;
          color: rgba(100, 200, 255, 0.9);
        }

        .final-score {
          font-family: 'Space Mono', monospace;
          font-size: 4rem;
          font-weight: 700;
          background: linear-gradient(135deg, #78c8ff 0%, #a0d8ff 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .session-feedback {
          font-size: 0.9rem;
          color: rgba(100, 200, 255, 0.8);
          padding: 0.5rem 1rem;
          background: rgba(100, 200, 255, 0.1);
          border-radius: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .next-level-hint {
          font-size: 0.85rem;
          color: rgba(255, 255, 255, 0.5);
        }

        .start-btn {
          padding: 1rem 3rem;
          font-size: 1rem;
          font-family: inherit;
          font-weight: 500;
          background: linear-gradient(135deg, rgba(100, 200, 255, 0.2) 0%, rgba(150, 180, 255, 0.2) 100%);
          border: 1px solid rgba(100, 200, 255, 0.3);
          color: rgba(100, 200, 255, 0.9);
          border-radius: 3rem;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 1rem;
        }

        .start-btn:hover {
          background: linear-gradient(135deg, rgba(100, 200, 255, 0.3) 0%, rgba(150, 180, 255, 0.3) 100%);
          border-color: rgba(100, 200, 255, 0.5);
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(100, 200, 255, 0.2);
        }

        /* Settings Panel - Minimal */
        .settings-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(5px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100;
          animation: fadeIn 0.2s ease;
        }

        .settings-panel {
          background: linear-gradient(135deg, rgba(20, 25, 35, 0.98) 0%, rgba(15, 20, 30, 0.98) 100%);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 1.5rem;
          padding: 2rem;
          min-width: 280px;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .settings-panel h3 {
          font-weight: 500;
          font-size: 1.2rem;
          color: rgba(255, 255, 255, 0.9);
        }

        .setting-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .setting-group > label:not(.checkbox-label) {
          font-size: 0.85rem;
          color: rgba(255, 255, 255, 0.5);
        }

        .setting-hint {
          font-size: 0.75rem;
          color: rgba(255, 255, 255, 0.4);
          margin-top: 0.25rem;
        }

        .setting-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .setting-buttons button {
          flex: 1;
          padding: 0.6rem;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 0.5rem;
          color: rgba(255, 255, 255, 0.6);
          cursor: pointer;
          font-family: inherit;
          font-size: 0.9rem;
          transition: all 0.2s ease;
        }

        .setting-buttons button:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .setting-buttons button.active {
          background: rgba(100, 200, 255, 0.2);
          border-color: rgba(100, 200, 255, 0.4);
          color: rgba(100, 200, 255, 0.9);
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          cursor: pointer;
        }

        .checkbox-label input {
          width: 18px;
          height: 18px;
          accent-color: rgba(100, 200, 255, 0.8);
        }

        .checkbox-label span {
          color: rgba(255, 255, 255, 0.7);
        }

        .close-settings {
          padding: 0.75rem;
          background: rgba(100, 200, 255, 0.2);
          border: 1px solid rgba(100, 200, 255, 0.3);
          border-radius: 0.75rem;
          color: rgba(100, 200, 255, 0.9);
          cursor: pointer;
          font-family: inherit;
          font-size: 0.95rem;
          transition: all 0.2s ease;
        }

        .close-settings:hover {
          background: rgba(100, 200, 255, 0.3);
        }

        /* Theme Selector */
        .theme-selector {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.5rem;
        }

        .theme-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.6rem 0.8rem;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 0.5rem;
          cursor: pointer;
          font-family: inherit;
          transition: all 0.2s ease;
        }

        .theme-btn:hover {
          background: rgba(255, 255, 255, 0.06);
          border-color: rgba(255, 255, 255, 0.15);
        }

        .theme-btn.active {
          border-color: var(--theme-color);
          background: color-mix(in srgb, var(--theme-color) 15%, transparent);
        }

        .theme-preview {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: var(--theme-color);
          box-shadow: 0 0 8px var(--theme-color);
        }

        .theme-name {
          font-size: 0.8rem;
          color: rgba(255, 255, 255, 0.7);
        }

        .theme-btn.active .theme-name {
          color: var(--theme-color);
        }

        /* Landing Page Styles */
        .landing-page {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.5rem;
          max-width: 600px;
          width: 100%;
          padding: 1rem;
        }

        .landing-header {
          text-align: center;
          margin-bottom: 0.5rem;
        }

        .landing-title {
          font-size: 2.8rem;
          font-weight: 300;
          letter-spacing: -0.02em;
          background: linear-gradient(135deg, #e0e8f0 0%, #78c8ff 50%, #a0d8ff 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 0.25rem;
        }

        .landing-tagline {
          font-size: 1rem;
          color: rgba(255, 255, 255, 0.4);
          font-weight: 300;
        }

        .landing-tabs {
          display: flex;
          gap: 0.25rem;
          background: rgba(255, 255, 255, 0.03);
          padding: 0.25rem;
          border-radius: 0.75rem;
          border: 1px solid rgba(255, 255, 255, 0.06);
        }

        .landing-tabs .tab {
          padding: 0.6rem 1rem;
          background: transparent;
          border: none;
          color: rgba(255, 255, 255, 0.5);
          cursor: pointer;
          font-family: inherit;
          font-size: 0.85rem;
          border-radius: 0.5rem;
          transition: all 0.2s ease;
        }

        .landing-tabs .tab:hover {
          color: rgba(255, 255, 255, 0.7);
          background: rgba(255, 255, 255, 0.05);
        }

        .landing-tabs .tab.active {
          background: rgba(100, 200, 255, 0.15);
          color: rgba(100, 200, 255, 0.9);
        }

        .landing-content {
          width: 100%;
          min-height: 320px;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: 1rem;
          padding: 1.5rem;
          overflow-y: auto;
          max-height: 400px;
        }

        .tab-content h2 {
          font-size: 1.3rem;
          font-weight: 500;
          margin-bottom: 1rem;
          color: rgba(255, 255, 255, 0.9);
        }

        .tab-content p {
          color: rgba(255, 255, 255, 0.7);
          line-height: 1.6;
          margin-bottom: 0.75rem;
        }

        .tab-content strong {
          color: rgba(100, 200, 255, 0.9);
        }

        .tab-content em {
          color: rgba(200, 150, 255, 0.9);
          font-style: normal;
        }

        /* Intro tab */
        .intro-visual {
          text-align: center;
          margin-bottom: 1rem;
        }

        .brain-icon {
          display: flex;
          justify-content: center;
          animation: float 3s ease-in-out infinite;
          color: var(--accent);
        }

        .brain-icon svg {
          stroke: var(--accent);
        }

        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        .complexity-note {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          background: rgba(255, 200, 100, 0.1);
          border: 1px solid rgba(255, 200, 100, 0.2);
          border-radius: 0.5rem;
          margin-top: 1rem;
        }

        .complexity-note .note-icon {
          display: flex;
          align-items: center;
          color: rgba(255, 200, 100, 0.9);
        }

        .complexity-note .note-icon svg {
          stroke: rgba(255, 200, 100, 0.9);
        }

        .complexity-note span:last-child {
          color: rgba(255, 200, 100, 0.9);
          font-size: 0.9rem;
        }

        /* Rules tab */
        .rule-block {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.06);
        }

        .rule-block:last-of-type {
          border-bottom: none;
        }

        .rule-number {
          width: 32px;
          height: 32px;
          min-width: 32px;
          background: rgba(100, 200, 255, 0.15);
          color: rgba(100, 200, 255, 0.9);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .rule-text strong {
          display: block;
          margin-bottom: 0.25rem;
          color: rgba(255, 255, 255, 0.9);
        }

        .rule-text p {
          margin: 0.25rem 0;
          font-size: 0.9rem;
        }

        .rule-text kbd {
          display: inline-block;
          padding: 0.15rem 0.5rem;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 0.25rem;
          font-family: 'Space Mono', monospace;
          font-size: 0.85rem;
        }

        .pro-tip {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          background: rgba(100, 200, 120, 0.1);
          border: 1px solid rgba(100, 200, 120, 0.2);
          border-radius: 0.5rem;
          margin-top: 0.5rem;
        }

        .pro-tip .tip-icon {
          display: flex;
          align-items: center;
          color: rgba(100, 200, 120, 0.9);
        }

        .pro-tip .tip-icon svg {
          stroke: rgba(100, 200, 120, 0.9);
        }

        .pro-tip span:last-child {
          color: rgba(100, 200, 120, 0.9);
          font-size: 0.9rem;
        }

        /* Benefits tab */
        .benefit-section {
          margin-bottom: 1.25rem;
        }

        .benefit-section h3 {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 1rem;
          font-weight: 500;
          margin-bottom: 0.5rem;
          color: rgba(255, 255, 255, 0.85);
        }

        .section-icon {
          display: inline-flex;
          align-items: center;
          color: rgba(100, 200, 255, 0.8);
        }

        .section-icon svg {
          stroke: rgba(100, 200, 255, 0.8);
        }

        .benefit-section ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .benefit-section li {
          position: relative;
          padding-left: 1.25rem;
          margin-bottom: 0.4rem;
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.9rem;
        }

        .benefit-section li::before {
          content: '→';
          position: absolute;
          left: 0;
          color: rgba(100, 200, 255, 0.6);
        }

        .research-note {
          background: rgba(100, 150, 255, 0.08);
          border: 1px solid rgba(100, 150, 255, 0.15);
          border-radius: 0.75rem;
          padding: 1rem;
        }

        .research-note h4 {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.95rem;
          margin-bottom: 0.5rem;
          color: rgba(255, 255, 255, 0.85);
        }

        .research-note p {
          font-size: 0.85rem;
          margin-bottom: 0.5rem;
        }

        .research-note .caveat {
          color: rgba(255, 200, 100, 0.8);
          font-style: italic;
        }

        .audio-status-landing {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.8rem;
          color: rgba(100, 200, 120, 0.7);
        }

        /* Start button */
        .start-training-btn {
          position: relative;
          padding: 1rem 3rem;
          font-size: 1.1rem;
          font-family: inherit;
          font-weight: 500;
          background: linear-gradient(135deg, rgba(100, 200, 255, 0.2) 0%, rgba(150, 180, 255, 0.2) 100%);
          border: 1px solid rgba(100, 200, 255, 0.3);
          color: rgba(100, 200, 255, 0.9);
          border-radius: 3rem;
          cursor: pointer;
          transition: all 0.3s ease;
          overflow: hidden;
        }

        .start-training-btn:hover {
          background: linear-gradient(135deg, rgba(100, 200, 255, 0.3) 0%, rgba(150, 180, 255, 0.3) 100%);
          border-color: rgba(100, 200, 255, 0.5);
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(100, 200, 255, 0.2);
        }

        .start-training-btn .btn-glow {
          position: absolute;
          inset: -2px;
          background: linear-gradient(90deg, transparent, rgba(100, 200, 255, 0.4), transparent);
          animation: btnGlow 2s ease-in-out infinite;
        }

        @keyframes btnGlow {
          0%, 100% { transform: translateX(-100%); }
          50% { transform: translateX(100%); }
        }

        .start-training-btn .btn-text {
          position: relative;
          z-index: 1;
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* ============================================ */
        /* TUTORIAL STYLES */
        /* ============================================ */
        .tutorial-container {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2rem;
          padding: 2rem;
          max-width: 500px;
          width: 100%;
          min-height: 500px;
        }

        .tutorial-progress {
          display: flex;
          gap: 0.5rem;
        }

        .progress-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.2);
          transition: all 0.3s ease;
        }

        .progress-dot.active {
          background: var(--accent);
          transform: scale(1.3);
        }

        .progress-dot.done {
          background: var(--success);
        }

        .tutorial-content {
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          flex: 1;
        }

        .tutorial-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          color: var(--accent);
          margin-bottom: 0.5rem;
        }

        .tutorial-icon.success {
          color: var(--success);
        }

        .icon-plus {
          font-size: 1.5rem;
          color: rgba(255, 255, 255, 0.4);
        }

        .tutorial-content h2 {
          font-size: 1.5rem;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.95);
        }

        .tutorial-content p {
          color: rgba(255, 255, 255, 0.7);
          line-height: 1.6;
          max-width: 400px;
        }

        .tutorial-expectation {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          background: rgba(255, 200, 100, 0.1);
          border: 1px solid rgba(255, 200, 100, 0.2);
          border-radius: 0.5rem;
          color: rgba(255, 200, 100, 0.9);
          font-size: 0.9rem;
        }

        .tutorial-highlight {
          padding: 0.75rem 1rem;
          background: rgba(100, 200, 255, 0.1);
          border: 1px solid rgba(100, 200, 255, 0.2);
          border-radius: 0.5rem;
          color: rgba(100, 200, 255, 0.9);
          font-size: 0.9rem;
        }

        .tutorial-actions {
          display: flex;
          gap: 1rem;
        }

        .tutorial-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          border-radius: 2rem;
          font-family: inherit;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .tutorial-btn.primary {
          background: linear-gradient(135deg, rgba(100, 200, 255, 0.2) 0%, rgba(150, 180, 255, 0.2) 100%);
          border: 1px solid rgba(100, 200, 255, 0.3);
          color: rgba(100, 200, 255, 0.9);
        }

        .tutorial-btn.primary:hover {
          background: linear-gradient(135deg, rgba(100, 200, 255, 0.3) 0%, rgba(150, 180, 255, 0.3) 100%);
          transform: translateY(-2px);
        }

        .tutorial-btn.secondary {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.6);
        }

        .tutorial-btn.secondary:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .tutorial-skip {
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.4);
          font-family: inherit;
          font-size: 0.85rem;
          cursor: pointer;
          padding: 0.5rem;
          margin-top: auto;
        }

        .tutorial-skip:hover {
          color: rgba(255, 255, 255, 0.6);
        }

        /* Tutorial Demo/Practice */
        .tutorial-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          padding: 0 1rem;
        }

        .tutorial-badge {
          font-family: 'Space Mono', monospace;
          font-size: 0.8rem;
          padding: 0.4rem 0.8rem;
          background: rgba(100, 200, 255, 0.15);
          border: 1px solid rgba(100, 200, 255, 0.3);
          border-radius: 1rem;
          color: rgba(100, 200, 255, 0.9);
        }

        .practice-progress {
          font-size: 0.85rem;
          color: rgba(255, 255, 255, 0.5);
        }

        .tutorial-game-area {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.5rem;
        }

        .tutorial-grid {
          position: relative;
          width: 250px;
          height: 250px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(20, 30, 50, 0.8) 0%, rgba(10, 15, 25, 0.4) 70%, transparent 100%);
        }

        .tutorial-node {
          position: absolute;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: rgba(60, 100, 140, 0.3);
          border: 1px solid rgba(100, 180, 240, 0.2);
          transform: translate(-50%, -50%);
          transition: all 0.3s ease;
        }

        .tutorial-node.active {
          background: radial-gradient(circle, rgba(100, 200, 255, 0.9) 0%, rgba(60, 150, 220, 0.6) 100%);
          box-shadow: 0 0 20px rgba(100, 200, 255, 0.8);
          border-color: rgba(100, 200, 255, 0.8);
        }

        .tutorial-node.previous {
          background: rgba(255, 200, 100, 0.4);
          border-color: rgba(255, 200, 100, 0.5);
        }

        .tutorial-letter {
          font-family: 'Space Mono', monospace;
          font-size: 2.5rem;
          font-weight: 700;
          color: rgba(100, 200, 255, 0.9);
          text-shadow: 0 0 20px rgba(100, 200, 255, 0.5);
        }

        .tutorial-hint {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          justify-content: center;
        }

        .hint-tag {
          padding: 0.5rem 1rem;
          border-radius: 1rem;
          font-size: 0.85rem;
          animation: fadeIn 0.3s ease;
        }

        .hint-tag.position {
          background: rgba(100, 200, 255, 0.2);
          border: 1px solid rgba(100, 200, 255, 0.4);
          color: rgba(100, 200, 255, 0.9);
        }

        .hint-tag.audio {
          background: rgba(200, 150, 255, 0.2);
          border: 1px solid rgba(200, 150, 255, 0.4);
          color: rgba(200, 150, 255, 0.9);
        }

        .hint-tag.neutral {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: rgba(255, 255, 255, 0.6);
        }

        .tutorial-feedback {
          padding: 0.75rem 1.5rem;
          border-radius: 1rem;
          font-size: 1rem;
          font-weight: 500;
          animation: fadeIn 0.3s ease;
        }

        .tutorial-feedback.correct {
          background: rgba(100, 200, 120, 0.2);
          border: 1px solid rgba(100, 200, 120, 0.4);
          color: rgba(100, 200, 120, 0.9);
        }

        .tutorial-feedback.incorrect {
          background: rgba(255, 100, 100, 0.2);
          border: 1px solid rgba(255, 100, 100, 0.4);
          color: rgba(255, 100, 100, 0.9);
        }

        .practice-buttons {
          display: flex;
          gap: 1rem;
        }

        .practice-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          padding: 1rem 2rem;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 1rem;
          color: rgba(255, 255, 255, 0.7);
          font-family: inherit;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .practice-btn:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.1);
          transform: translateY(-2px);
        }

        .practice-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .practice-btn.correct {
          background: rgba(100, 200, 120, 0.2);
          border-color: rgba(100, 200, 120, 0.5);
          color: rgba(100, 200, 120, 1);
        }

        .practice-btn.incorrect {
          background: rgba(255, 100, 100, 0.2);
          border-color: rgba(255, 100, 100, 0.5);
          color: rgba(255, 100, 100, 1);
        }

        .practice-btn kbd {
          font-family: 'Space Mono', monospace;
          font-size: 0.75rem;
          padding: 0.2rem 0.5rem;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 0.25rem;
        }

        .practice-wait {
          color: rgba(255, 255, 255, 0.5);
          font-size: 0.9rem;
        }

        /* Tutorial Complete */
        .tutorial-complete {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          text-align: center;
        }

        .tutorial-score {
          font-family: 'Space Mono', monospace;
          font-size: 1.2rem;
          color: var(--success);
        }

        .tutorial-tip {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          background: rgba(100, 200, 120, 0.1);
          border: 1px solid rgba(100, 200, 120, 0.2);
          border-radius: 0.5rem;
          color: rgba(100, 200, 120, 0.9);
          font-size: 0.85rem;
          margin: 1rem 0;
        }

        /* Streak display */
        .streak-badge {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.3rem 0.8rem;
          background: rgba(255, 150, 50, 0.15);
          border: 1px solid rgba(255, 150, 50, 0.3);
          border-radius: 1rem;
          color: rgba(255, 150, 50, 0.9);
          font-family: 'Space Mono', monospace;
          font-size: 0.8rem;
        }

        .streak-badge svg {
          color: rgba(255, 150, 50, 0.9);
        }

        /* Icon Button */
        .icon-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          color: rgba(255, 255, 255, 0.5);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .icon-btn:hover {
          background: rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.8);
        }

        /* Analytics Dashboard */
        .analytics-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100;
          animation: fadeIn 0.2s ease;
          padding: 1rem;
        }

        .analytics-panel {
          background: linear-gradient(135deg, rgba(20, 25, 35, 0.98) 0%, rgba(15, 20, 30, 0.98) 100%);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 1.5rem;
          padding: 1.5rem;
          max-width: 480px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .analytics-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .analytics-header h3 {
          font-weight: 500;
          font-size: 1.2rem;
          color: rgba(255, 255, 255, 0.9);
        }

        .close-btn {
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.5);
          font-size: 1.5rem;
          cursor: pointer;
          padding: 0.25rem;
          line-height: 1;
        }

        .close-btn:hover {
          color: rgba(255, 255, 255, 0.8);
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
        }

        .stat-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 0.75rem;
          padding: 1rem;
          text-align: center;
        }

        .stat-value {
          font-family: 'Space Mono', monospace;
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--primary);
        }

        .stat-label {
          font-size: 0.75rem;
          color: rgba(255, 255, 255, 0.5);
          margin-top: 0.25rem;
        }

        .trend-section, .heatmap-section {
          background: rgba(255, 255, 255, 0.02);
          border-radius: 0.75rem;
          padding: 1rem;
        }

        .trend-section h4, .heatmap-section h4 {
          font-size: 0.85rem;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 0.75rem;
        }

        .trend-chart {
          display: flex;
          align-items: flex-end;
          gap: 4px;
          height: 100px;
        }

        .trend-bar {
          flex: 1;
          background: linear-gradient(to top, var(--primary), var(--accent));
          border-radius: 3px 3px 0 0;
          min-height: 4px;
          position: relative;
          transition: height 0.3s ease;
        }

        .trend-bar:hover {
          filter: brightness(1.2);
        }

        .trend-label {
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          font-size: 0.6rem;
          color: rgba(255, 255, 255, 0.6);
          opacity: 0;
          transition: opacity 0.2s;
        }

        .trend-bar:hover .trend-label {
          opacity: 1;
        }

        .trend-axis {
          display: flex;
          justify-content: space-between;
          margin-top: 0.5rem;
          font-size: 0.7rem;
          color: rgba(255, 255, 255, 0.4);
        }

        .heatmap {
          display: grid;
          grid-template-columns: repeat(10, 1fr);
          gap: 3px;
        }

        .heatmap-cell {
          aspect-ratio: 1;
          background: var(--primary);
          border-radius: 2px;
        }

        .heatmap-legend {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          margin-top: 0.5rem;
          font-size: 0.7rem;
          color: rgba(255, 255, 255, 0.4);
        }

        .legend-cells {
          display: flex;
          gap: 2px;
        }

        .legend-cell {
          width: 12px;
          height: 12px;
          background: var(--primary);
          border-radius: 2px;
        }

        .insight-card {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem;
          background: rgba(255, 255, 255, 0.03);
          border-radius: 0.5rem;
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.85rem;
        }

        .insight-card svg {
          color: var(--primary);
          flex-shrink: 0;
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          padding: 2rem;
          text-align: center;
          color: rgba(255, 255, 255, 0.5);
        }

        .empty-state svg {
          opacity: 0.3;
        }
      `}</style>

      <div className="app-container">
        <NeuralBackground intensity={gameState === 'playing' ? 0.8 : 0.5} />

        {/* AI Coach tip - fades in at top */}
        <CoachTip tip={coachTip} visible={showCoachTip} />

        {/* Tutorial for first-time users */}
        {showTutorial && gameState === 'idle' && (
          <Tutorial
            onComplete={handleTutorialComplete}
            audioEngine={audioEngine}
          />
        )}

        {/* Main game container - hidden during tutorial */}
        {!showTutorial && (
          <div className="game-container">
            <div className="header">
              <span className="logo">Neural Sync</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {streak.current > 0 && (
                  <div className="streak-badge">
                    <Icons.Flame size={14} />
                    <span>{streak.current}</span>
                  </div>
                )}
                <button className="icon-btn" onClick={() => setIsMuted(!isMuted)} title={isMuted ? "Unmute Audio" : "Mute Audio"} aria-label={isMuted ? "Unmute audio" : "Mute audio"}>
                  {isMuted ? <Icons.VolumeX size={18} /> : <Icons.Volume size={18} />}
                </button>
                <button className="icon-btn" onClick={() => setShowAnalytics(true)} title="View Progress" aria-label="View analytics">
                  <Icons.BarChart size={18} />
                </button>
                <button className="settings-btn" onClick={() => setShowSettings(true)} aria-label="Open settings">
                  Settings
                </button>
              </div>
            </div>

            {gameState === 'idle' && (
              <LandingPage
                settings={settings}
                onUpdateSettings={setSettings}
                onStart={startGame}
                audioReady={audioReady}
              />
            )}

          {gameState === 'playing' && (
            <>
              <OrganicGrid
                activePosition={currentPosition}
                trail={positionHistory.slice(-settings.nLevel - 1)}
              />
              <div className="letter-display" key={currentLetter}>
                {currentLetter}
              </div>
              <ResponseButtons
                onResponse={handleResponse}
                disabled={false}
                feedback={feedback}
              />
            </>
          )}

          {gameState === 'complete' && (
            <div className="complete-screen">
              <h2 className="complete-title">Session Complete</h2>
              <div className="final-score">
                {stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0}%
              </div>
              {settings.aiCoach && sessionFeedback && (
                <div className="session-feedback">
                  <Icons.Sparkles size={14} />
                  <span>{sessionFeedback}</span>
                </div>
              )}
              <span className="next-level-hint">
                Next: {settings.nLevel}-Back
              </span>
              <button className="start-btn" onClick={startGame} aria-label="Continue training">
                Continue
              </button>
            </div>
          )}
        </div>
        )}

        {!showTutorial && gameState !== 'idle' && (
          <StatsDisplay
            stats={stats}
            nLevel={settings.nLevel}
            isExpanded={showStats}
            onToggle={() => setShowStats(!showStats)}
          />
        )}

        <SettingsPanel
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          settings={settings}
          onUpdate={setSettings}
        />

        <AnalyticsDashboard
          isOpen={showAnalytics}
          onClose={() => setShowAnalytics(false)}
          sessions={sessions}
          streak={streak}
        />
      </div>
    </>
  );
}

export { ErrorBoundary };
